import React, { useEffect, useState } from "react";
import { Megaphone, PlayCircle } from "lucide-react";
import axios from "axios";

// Live ads carousel. Fetches active campaigns from /api/ads/live?slot=…
// Public endpoint — used by anonymous visitors on the homepage. We use the
// raw axios (not the credentialed `api`) so anonymous users don't send auth
// cookies. `slot` is one of: hoarding | banner | top_listing | video_ad.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Ad clicks route through /api/ads/{id}/click for CTR tracking, which then 302s to the
// real destination. Fall back to the direct target if the click_url is missing (older campaigns).
const adHref = (ad) =>
  ad?.click_url ? `${process.env.REACT_APP_BACKEND_URL}${ad.click_url}` :
    (ad?.target_url || (ad?.listing_id ? `/listing/${ad.listing_id}` : "#"));

export default function LiveAdsSlot({ slot, category, city, limit = 3, className = "" }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const params = { slot, limit };
    if (category) params.category = category;
    if (city) params.city = city;
    axios.get(`${API}/ads/live`, { params }).then(r => {
      setItems(r.data?.items || []);
    }).catch(() => setItems([]));
  }, [slot, category, city, limit]);

  if (!items.length) return null;

  if (slot === "hoarding") {
    // Full-width hero banner card at the top of the page.
    const ad = items[0];
    return (
      <a
        href={adHref(ad)}
        className={`block relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#E05E36] via-[#c94e2a] to-[#8B5E34] ring-1 ring-black/5 shadow-md ${className}`}
        data-testid={`live-ad-hoarding-${ad.campaign_id}`}
      >
        {ad.creative_image_url ? (
          <img src={ad.creative_image_url} alt={ad.creative_headline || "Sponsored"}
               loading="lazy"
               className="w-full h-40 md:h-56 object-cover opacity-90" />
        ) : (
          <div className="w-full h-40 md:h-56" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-2.5 py-1 text-[10px] font-black text-white uppercase tracking-widest">
          <Megaphone size={11} /> Sponsored
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <div className="heading text-xl md:text-3xl font-extrabold text-white drop-shadow">
            {ad.creative_headline || "Discover a new local business"}
          </div>
          {ad.creative_cta && (
            <span className="mt-2 inline-block rounded-full bg-white text-[#E05E36] font-black text-xs px-4 py-1.5">
              {ad.creative_cta}
            </span>
          )}
        </div>
      </a>
    );
  }

  if (slot === "banner") {
    // Slim horizontal card, up to 2 side-by-side.
    return (
      <div className={`grid sm:grid-cols-${Math.min(items.length, 2)} gap-3 ${className}`} data-testid="live-ad-banners">
        {items.slice(0, 2).map(ad => (
          <a
            key={ad.campaign_id}
            href={adHref(ad)}
            className="relative overflow-hidden rounded-2xl border border-stone-200 bg-white hover:border-[#E05E36]/70 transition-colors flex items-center"
            data-testid={`live-ad-banner-${ad.campaign_id}`}
          >
            {ad.creative_image_url && (
              <img src={ad.creative_image_url} alt="" className="w-28 h-20 object-cover shrink-0" loading="lazy" />
            )}
            <div className="p-3 flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-black text-[#8B5E34]">Sponsored</div>
              <div className="font-bold text-stone-900 truncate">{ad.creative_headline || "Featured"}</div>
              {ad.creative_cta && (
                <div className="text-xs font-bold text-[#E05E36] mt-0.5">{ad.creative_cta} →</div>
              )}
            </div>
          </a>
        ))}
      </div>
    );
  }

  if (slot === "video_ad") {
    const ad = items[0];
    if (!ad.creative_video_url) return null;
    return (
      <div className={`rounded-3xl overflow-hidden border border-stone-200 bg-black ${className}`} data-testid={`live-ad-video-${ad.campaign_id}`}>
        <div className="relative">
          <video
            src={ad.creative_video_url}
            className="w-full aspect-video object-cover"
            controls
            playsInline
            preload="metadata"
          />
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/70 text-white text-[10px] font-black uppercase tracking-widest px-2 py-0.5">
            <PlayCircle size={11} /> Sponsored
          </span>
        </div>
        {ad.creative_headline && (
          <div className="p-3 text-white bg-black/90 flex items-center justify-between">
            <div className="font-bold text-sm truncate">{ad.creative_headline}</div>
            {ad.creative_cta && ad.target_url && (
              <a href={adHref(ad)} className="text-xs font-black text-[#E05E36] bg-white rounded-full px-3 py-1">{ad.creative_cta}</a>
            )}
          </div>
        )}
      </div>
    );
  }

  // top_listing slot — pin sponsored listings above organic. Renders a compact list.
  return (
    <div className={`space-y-2 ${className}`} data-testid="live-ad-top-listings">
      {items.map(ad => (
        <a
          key={ad.campaign_id}
          href={adHref(ad)}
          className="flex items-center gap-3 p-3 rounded-2xl border-2 border-[#E05E36]/60 bg-orange-50/40 hover:bg-orange-50 transition-colors"
          data-testid={`live-ad-top-${ad.campaign_id}`}
        >
          {ad.listing?.images?.[0] && (
            <img src={ad.listing.images[0]} alt="" className="w-14 h-14 rounded-xl object-cover" loading="lazy" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-black text-[#E05E36]">Sponsored</div>
            <div className="font-bold text-stone-900 truncate">{ad.listing?.title || ad.creative_headline}</div>
            <div className="text-xs text-stone-500 truncate">{ad.listing?.city || ""}</div>
          </div>
        </a>
      ))}
    </div>
  );
}
